import { Routing, Search } from "@common/_constants";
import { MessageService } from "@common/_services";
import { Button } from "@common/components/Atoms/Button";
import Error from "@common/components/Atoms/Error";
import {
    IconMessageForward,
    IconMessageRead,
    IconMessageReply,
    IconMessageUnread,
} from "@common/components/Atoms/Icons";
import Loading from "@common/components/Atoms/Loading";
import LocalTime from "@common/components/Atoms/LocalTime";
import { SearchEntry } from "@common/components/Atoms/SearchFilters";
import Showing from "@common/components/Atoms/Showing";
import Modal from "@common/components/Molecules/Modal";
import Pagination from "@common/components/Molecules/Pagination";
import { Message as MessageModel } from "@common/models";
import { GlobalConfig } from "@common/utils";
import { SearchOptions, SearchService } from "@pro/_services";
import { MessagesPageHeaderCenter } from "@pro/components/Organisms/Messages";
import { SendMessage } from "@pro/containers/Modals/SendMessage";
import { PageInternal } from "@pro/containers/PageInternal";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

interface MessagesProps extends RouteComponentProps<any> {
    handleMessageStatus: () => void;
}

interface MessagesState {
    unreadCount: number;
    totalCount: number;
    error: boolean;
    errorMessage: string;
    loading: boolean;
    isLoading: boolean;
    updating: boolean;
    search: SearchOptions;
    data: {
        results: MessageModel[];
        current_page: number;
        page_size: number;
        total: number;
    };
    sendMessageModalIsOpen: boolean;
    filtersModalIsOpen: boolean;
    messageReadStates: {};
    contextJobIdForModal: number;
    contextJobNameForModal: string;
    contextUserIdForModal: number;
    contextUserFullNameForModal: string;
    isLargeScreen: boolean;
    triggerMessageIndicator: boolean;
    toUserIdsForModal: number[];
    location: string;
    messageIdForModal: number;
    jobCandidateForModals: any;
    keyword: string;
    initialMessageBody: string;
}

export class Messages extends React.Component<MessagesProps, MessagesState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    removeHistoryListener: () => void;
    searchService: SearchService;

    constructor(props) {
        super(props);

        this.state = {
            keyword: "",
            totalCount: 0,
            error: false,
            errorMessage: "",
            loading: true,
            updating: false,
            search: this.emptySearchOptions(),
            data: {
                current_page: 1,
                page_size: 25,
                results: [] as MessageModel[],
                total: 0,
            },
            sendMessageModalIsOpen: false,
            filtersModalIsOpen: false,
            messageReadStates: {},
            contextJobIdForModal: 0,
            contextJobNameForModal: "",
            contextUserIdForModal: 0,
            contextUserFullNameForModal: "",
            isLargeScreen: false,
            triggerMessageIndicator: false,
            isLoading: false,
            jobCandidateForModals: {},
            location: "",
            messageIdForModal: 0,
            toUserIdsForModal: [] as number[],
            unreadCount: 0,
            initialMessageBody: "",
        };

        this.onPageChange = this.onPageChange.bind(this);
        this.onKeywordSubmit = this.onKeywordSubmit.bind(this);
        this.onJobSearch = this.onJobSearch.bind(this);
        this.onCandidateSearch = this.onCandidateSearch.bind(this);
        this.onUrlChange = this.onUrlChange.bind(this);

        this.onReply = this.onReply.bind(this);
        this.onForward = this.onForward.bind(this);
        this.onMarkReadStatus = this.onMarkReadStatus.bind(this);
        this.onFilterTypeChanged = this.onFilterTypeChanged.bind(this);

        this.emptySearchOptions = this.emptySearchOptions.bind(this);
        this.fetchMessages = this.fetchMessages.bind(this);

        this.searchService = new SearchService(
            this.emptySearchOptions,
            this.fetchMessages,
            this.props.history
        );
    }

    componentDidMount() {
        this.searchService.updateFromUrl(this.props.location);
        this.removeHistoryListener = this.props.history.listen(
            this.onUrlChange
        );
        this.setState({
            isLargeScreen: window.matchMedia(
                "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
            ).matches,
        });
        window.onresize = () => {
            this.setState({
                isLargeScreen: window.matchMedia(
                    "(min-width:" + GlobalConfig.ui.breakpoints.md + "px)"
                ).matches,
            });
        };
    }

    componentWillUnmount() {
        if (this.removeHistoryListener) this.removeHistoryListener();
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    emptySearchOptions() {
        return {
            context_job_id: undefined,
            context_user_id: undefined,
            search_term: "",
            current_page: 1,
            page_size: 25,
            filter_type: Search.MessageFilter.MY_ALL_MESSAGES,
        };
    }

    onUrlChange(location) {
        this.searchService.updateFromUrl(location);
    }

    onReply(msg: MessageModel) {
        this.setState({
            contextJobIdForModal: msg.context_job_id,
            contextJobNameForModal: msg.context_job_name,
            contextUserIdForModal: msg.context_user_id,
            contextUserFullNameForModal: msg.context_user_full_name,
            toUserIdsForModal: [msg.from_user_id],
            messageIdForModal: msg.message_id,
            initialMessageBody: this.composeInitialMessageBody(
                "reply",
                msg.body
            ),
            sendMessageModalIsOpen: true,
        });
    }

    onForward(msg: MessageModel) {
        this.setState({
            contextJobIdForModal: msg.context_job_id,
            contextJobNameForModal: msg.context_job_name,
            contextUserIdForModal: msg.context_user_id,
            contextUserFullNameForModal: msg.context_user_full_name,
            toUserIdsForModal: [] as number[],
            messageIdForModal: 0,
            initialMessageBody: this.composeInitialMessageBody(
                "forward",
                msg.body
            ),
            sendMessageModalIsOpen: true,
        });
    }

    onMarkReadStatus(messageId: number, isRead: boolean) {
        const messageReadStates = this.state.messageReadStates;
        messageReadStates[messageId] = isRead;
        this.setState(prevState => ({
            messageReadStates,
            unreadCount: isRead
                ? prevState.unreadCount - 1
                : prevState.unreadCount + 1,
        }));

        MessageService.markReadStatus(messageId, isRead).then(result => {
            this.setState(prevState => {
                return {
                    triggerMessageIndicator: !prevState.triggerMessageIndicator,
                };
            });

            if (!!this.props.handleMessageStatus) {
                this.props.handleMessageStatus();
            }
        });
    }

    composeInitialMessageBody(type: "reply" | "forward", messageBody: string) {
        const actionType =
            type === "reply"
                ? "-------------- Reply to message -------------"
                : "-------------- Forwarded message -------------";
        return "\r\n" + `${actionType}\r\n` + messageBody;
    }

    onPageChange(pageNumber: number) {
        this.searchService.updateUrl({
            ...this.state.search,
            current_page: pageNumber,
        });
    }

    onKeywordSubmit(keyword: string) {
        this.closeModal("filtersModalIsOpen");
        // A keyword search resets everything...
        this.searchService.updateUrl({
            ...this.emptySearchOptions(),
            search_term: keyword,
        });
    }

    onJobSearch() {
        // TODO
    }
    onCandidateSearch() {
        // TODO
    }

    onFilterTypeChanged(event: any) {
        this.searchService.updateUrl({
            ...this.state.search,
            filter_type: Number(event.target.value),
        });
    }

    fetchMessages(options: SearchOptions) {
        this.setState({ updating: true }, () => {
            // Check to see if there already was an abortController, and if so, call abort on it
            if (this._abortControllers.search) {
                this._abortControllers.search.abort();
            }
            this._abortControllers.search = new AbortController();
            const searchSignal = this._abortControllers.search.signal;

            MessageService.search(options, searchSignal)
                .then(result => {
                    //update read status for all messages shown
                    const messageReadStates = {};
                    result.results.forEach(msg => {
                        messageReadStates[msg.message_id] = msg.is_read;
                    });

                    const defaultSearch = this.emptySearchOptions();
                    const search = {
                        context_job_id: defaultSearch.context_job_id,
                        context_user_id: defaultSearch.context_user_id,
                        search_term:
                            result.search_term || defaultSearch.search_term,
                        current_page:
                            result.current_page || defaultSearch.current_page,
                        page_size: result.page_size || defaultSearch.page_size,
                        filter_type:
                            result.filter_type || defaultSearch.filter_type,
                    };

                    this.setState(
                        prevState => ({
                            totalCount: result.total,
                            unreadCount: result.unread_count,
                            error: false,
                            loading: false,
                            updating: false,
                            data: result,
                            messageReadStates,
                            search,
                        }),
                        () => {
                            window.scrollTo(0, 0);
                        }
                    );
                })
                .catch(err => {
                    console.log(err);
                    if (err.type === "abort" || err.name === "AbortError") {
                        this.setState({ updating: false, loading: false });
                        return;
                    }

                    this.setState({
                        error: true,
                        errorMessage: err.message,
                        updating: false,
                        loading: false,
                    });
                });
        });
    }

    openModal(modal, jobCandidateForModals = this.state.jobCandidateForModals) {
        const state = { jobCandidateForModals };
        state[modal] = true;
        this.setState(state);
    }
    closeModal(modal) {
        const state = {};
        state[modal] = false;
        this.setState(state);
    }

    render() {
        const { loading, error, updating, isLargeScreen } = this.state;

        const noMessageTemplate = message => {
            return (
                <PageInternal title="messages">
                    <h2 className="c-heading c-heading--purple">{message}</h2>
                    <div className="u-text-center">
                        <div className="c-block c-block--space-evenly c-block--narrow">
                            <Link
                                className="c-button c-button--green"
                                to={Routing.Client.Pro.Messages.SEARCH}
                            >
                                search again
                            </Link>
                        </div>
                    </div>
                </PageInternal>
            );
        };

        if (error) {
            return (
                <PageInternal title="messages">
                    <Error
                        show={this.state.error}
                        description={this.state.errorMessage}
                    />
                </PageInternal>
            );
        } else if (loading) {
            return (
                <PageInternal title="messages">
                    <Loading />
                </PageInternal>
            );
        } else if (this.state.data.results.length === 0) {
            if (
                this.state.search.filter_type !==
                Search.MessageFilter.MY_UNREAD_MESSAGES
            ) {
                return noMessageTemplate(
                    "we didn't find any messages matching your search."
                );
            } else {
                return noMessageTemplate("you don't have any unread messages.");
            }
        }

        return (
            <PageInternal
                updating={updating}
                title="messages"
                triggerMessageIndicator={this.state.triggerMessageIndicator}
                center={
                    <MessagesPageHeaderCenter
                        keyword={this.state.search.search_term || ""}
                        onKeywordSubmit={this.onKeywordSubmit}
                    />
                }
            >
                {/* TODO: put these back when the searches are fixed
                onJobSearchSubmit={this.onJobSearch}
                onCandidateSearchSubmit={this.onCandidateSearch}/> */}

                <section
                    className={
                        "c-filtered-list__view c-filtered-list__view" +
                        (isLargeScreen ? "--table" : "--cards")
                    }
                >
                    {isLargeScreen && (
                        <section className="c-filtered-list__filters">
                            <header className="c-filtered-list__header">
                                <div className="c-filtered-list__header-title">
                                    refine
                                </div>
                            </header>
                            <div className="c-facets">
                                <div className="c-facets__group">
                                    <div className="c-facets__options">
                                        <div className="c-facets__option">
                                            <label
                                                className="c-facets__radio"
                                                htmlFor="msg_refine_all"
                                            >
                                                <input
                                                    type="radio"
                                                    id="msg_refine_all"
                                                    value={
                                                        Search.MessageFilter
                                                            .MY_ALL_MESSAGES
                                                    }
                                                    checked={
                                                        this.state.search
                                                            .filter_type ===
                                                        Search.MessageFilter
                                                            .MY_ALL_MESSAGES
                                                    }
                                                    onChange={
                                                        this.onFilterTypeChanged
                                                    }
                                                />
                                                <span className="c-facets__label">
                                                    all messages
                                                </span>
                                                {/* <span className="c-facets__count">{this.state.totalCount}</span> */}
                                            </label>
                                        </div>
                                        <div className="c-facets__option">
                                            <label
                                                className="c-facets__radio"
                                                htmlFor="msg_refine_unread"
                                            >
                                                <input
                                                    type="radio"
                                                    id="msg_refine_unread"
                                                    value={
                                                        Search.MessageFilter
                                                            .MY_UNREAD_MESSAGES
                                                    }
                                                    checked={
                                                        this.state.search
                                                            .filter_type ===
                                                        Search.MessageFilter
                                                            .MY_UNREAD_MESSAGES
                                                    }
                                                    onChange={
                                                        this.onFilterTypeChanged
                                                    }
                                                />
                                                <span className="c-facets__label">
                                                    unread
                                                </span>
                                                {/* <span className="c-facets__count">{this.state.unreadCount}</span> */}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                    <section className="c-filtered-list__table">
                        <header className="c-filtered-list__header">
                            <div className="c-filtered-list__header-title">
                                <Showing
                                    currentPage={this.state.data.current_page}
                                    pageSize={this.state.data.page_size}
                                    total={this.state.data.total}
                                />
                            </div>
                            {!isLargeScreen && (
                                <div className="c-filtered-list__header-actions">
                                    <Button
                                        id="open-filters-modal-button"
                                        onClick={() => {
                                            this.openModal(
                                                "filtersModalIsOpen"
                                            );
                                        }}
                                    >
                                        filters
                                    </Button>
                                </div>
                            )}
                        </header>

                        {this.state.data.results.length > 0 &&
                            !this.state.isLoading &&
                            this.state.data.results.map((msg, index) => (
                                <div
                                    className={
                                        "c-message " +
                                        (this.state.messageReadStates[
                                            msg.message_id
                                        ] || msg.current_user_is_owner
                                            ? "read"
                                            : "")
                                    }
                                    key={index}
                                >
                                    <div className="c-message__meta">
                                        <div className="c-message__from">
                                            {msg.from_name}
                                        </div>
                                        {!msg.is_direct_message && (
                                            <React.Fragment>
                                                <Link
                                                    className="c-message__job c-link c-link--magenta"
                                                    to={`/jobs/${msg.context_job_id}`}
                                                >
                                                    {msg.context_job_name}
                                                </Link>
                                                <Link
                                                    className="c-message__candidate c-link c-link--magenta"
                                                    to={`/referrals/${msg.context_user_id}/${msg.context_job_id}`}
                                                >
                                                    {msg.context_user_full_name}
                                                </Link>
                                            </React.Fragment>
                                        )}
                                    </div>
                                    <div className="c-message__container">
                                        <div className="c-message__content">
                                            <div className="c-message__date">
                                                <LocalTime
                                                    utcTimestamp={
                                                        msg.sent_timestamp
                                                    }
                                                />
                                            </div>
                                            <div className="c-message__body">
                                                {msg.subject && (
                                                    <span className="c-message__subject">
                                                        {msg.subject}
                                                    </span>
                                                )}
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: msg.body,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="c-message__actions">
                                            {!msg.current_user_is_owner && (
                                                <span>
                                                    {!this.state
                                                        .messageReadStates[
                                                        msg.message_id
                                                    ] && (
                                                        <a
                                                            onClick={() => {
                                                                this.onMarkReadStatus(
                                                                    msg.message_id,
                                                                    true
                                                                );
                                                            }}
                                                            title="mark read"
                                                        >
                                                            <IconMessageRead />
                                                            mark read
                                                        </a>
                                                    )}
                                                    {this.state
                                                        .messageReadStates[
                                                        msg.message_id
                                                    ] && (
                                                        <a
                                                            onClick={() => {
                                                                this.onMarkReadStatus(
                                                                    msg.message_id,
                                                                    false
                                                                );
                                                            }}
                                                            title="mark  unread"
                                                        >
                                                            <IconMessageUnread />
                                                            mark unread
                                                        </a>
                                                    )}
                                                    <a
                                                        onClick={() => {
                                                            this.onReply(msg);
                                                        }}
                                                        title="reply"
                                                    >
                                                        <IconMessageReply />
                                                        reply
                                                    </a>
                                                </span>
                                            )}
                                            {!msg.is_direct_message && (
                                                <a
                                                    onClick={() => {
                                                        this.onForward(msg);
                                                    }}
                                                    title="forward"
                                                >
                                                    <IconMessageForward />
                                                    forward
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                        {this.state.isLoading && <Loading />}

                        {this.state.data.results.length > 0 && (
                            <Pagination
                                currentPage={this.state.data.current_page}
                                pageSize={this.state.data.page_size}
                                total={this.state.data.total}
                                onPageChange={page =>
                                    this.onPageChange(page.selected + 1)
                                }
                            />
                        )}
                    </section>
                </section>

                {this.state.sendMessageModalIsOpen && (
                    <SendMessage
                        isOpen={this.state.sendMessageModalIsOpen}
                        contextJobId={this.state.contextJobIdForModal}
                        contextJobName={this.state.contextJobNameForModal}
                        contextUserId={this.state.contextUserIdForModal}
                        contextUserFullName={
                            this.state.contextUserFullNameForModal
                        }
                        toUserIds={this.state.toUserIdsForModal}
                        messageId={this.state.messageIdForModal}
                        initialMessageBody={this.state.initialMessageBody}
                        onCancel={() =>
                            this.closeModal("sendMessageModalIsOpen")
                        }
                        closeModal={() =>
                            this.closeModal("sendMessageModalIsOpen")
                        }
                    />
                )}

                <Modal
                    className="c-modal--filters"
                    size="full"
                    isOpen={this.state.filtersModalIsOpen}
                    closeModal={() => this.closeModal("filtersModalIsOpen")}
                >
                    <div className="c-modal__filters">
                        <div className="c-modal__filters-title">filters</div>
                        {/* TODO: put these back when the searches are fixed
              <SearchJobs overlay dark onSubmit={this.onJobSearch} />
              <SearchCandidates overlay dark onSubmit={this.onCandidateSearch} /> */}
                        <SearchEntry
                            label="keywords"
                            overlay={true}
                            dark={true}
                            submitOnEnter={true}
                            value={this.state.keyword}
                            onSubmit={this.onKeywordSubmit}
                        />

                        <div className="c-facets c-facets--overlay">
                            <div className="c-facets__group">
                                <div className="c-facets__options">
                                    <div className="c-facets__option">
                                        <label
                                            className="c-facets__radio"
                                            htmlFor="msg_refine_all"
                                        >
                                            <input
                                                type="radio"
                                                id="msg_refine_all"
                                                value={
                                                    Search.MessageFilter
                                                        .MY_ALL_MESSAGES
                                                }
                                                checked={
                                                    this.state.search
                                                        .filter_type ===
                                                    Search.MessageFilter
                                                        .MY_ALL_MESSAGES
                                                }
                                                onChange={
                                                    this.onFilterTypeChanged
                                                }
                                            />
                                            <span className="c-facets__label">
                                                all messages
                                            </span>
                                            {/* <span className="c-facets__count">{this.state.data.total}</span> */}
                                        </label>
                                    </div>
                                    <div className="c-facets__option">
                                        <label
                                            className="c-facets__radio"
                                            htmlFor="msg_refine_unread"
                                        >
                                            <input
                                                type="radio"
                                                id="msg_refine_unread"
                                                value={
                                                    Search.MessageFilter
                                                        .MY_UNREAD_MESSAGES
                                                }
                                                checked={
                                                    this.state.search
                                                        .filter_type ===
                                                    Search.MessageFilter
                                                        .MY_UNREAD_MESSAGES
                                                }
                                                onChange={
                                                    this.onFilterTypeChanged
                                                }
                                            />
                                            <span className="c-facets__label">
                                                unread
                                            </span>
                                            {/* <span className="c-facets__count">{this.state.data.unread_count}</span> */}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            </PageInternal>
        );
    }
}
